import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ViewListIcon from '@mui/icons-material/ViewList';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import type { MenuDay } from '@core/interfaces/api';

import { formatDate } from '../utils/date.utils';
import { MenuRowsView } from './menu-rows-view';
import { MenuSlidesView } from './menu-slides-view';
import { useMenuQuery } from '../queries/menus.queries';
import { useSyncMenuMutation } from '../queries/menus.mutations';
import { MenuPdfDialog } from '../pdf/menu-pdf-dialog';

type MenuViewMode = 'rows' | 'slides';

interface SiteMenusPageProps {
  siteId: number;
  siteName?: string;
  menuBugImageUrl?: string | null;
}

function SiteMenusPage({ siteId, siteName, menuBugImageUrl }: SiteMenusPageProps) {
  const {
    data: menu,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useMenuQuery({
    siteId,
    enabled: Boolean(siteId),
  });

  const { mutateAsync: syncMenu, isPending: isSyncing } = useSyncMenuMutation();

  const [propertyId, setPropertyId] = useState('');
  const [cycleStartDate, setCycleStartDate] = useState('');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<MenuViewMode>('rows');
  const [pdfOpen, setPdfOpen] = useState(false);

  useEffect(() => {
    if (menu) {
      setPropertyId(menu.propertyId ? String(menu.propertyId) : '');
      setCycleStartDate(menu.cycleStartDate || '');
    } else if (!isLoading && !isFetching && !isError) {
      setPropertyId('');
      setCycleStartDate('');
    }
  }, [menu, isLoading, isFetching, isError]);

  useEffect(() => {
    if (!syncSuccess) return;
    const timeoutId = window.setTimeout(() => setSyncSuccess(false), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [syncSuccess]);

  useEffect(() => {
    if (!saveSuccess) return;
    const timeoutId = window.setTimeout(() => setSaveSuccess(false), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [saveSuccess]);

  const handleSync = async () => {
    if (!propertyId || !cycleStartDate) {
      setSyncError('Property ID and cycle start date are required.');
      return;
    }

    const parsedPropertyId = parseInt(propertyId, 10);
    if (Number.isNaN(parsedPropertyId)) {
      setSyncError('Property ID must be a number.');
      return;
    }

    try {
      setSyncError(null);
      await syncMenu({
        siteId,
        propertyId: parsedPropertyId,
        cycleStartDate,
      });
      setSyncSuccess(true);
      await refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Unable to sync menu.';
      setSyncError(message);
      setSyncSuccess(false);
    }
  };

  const isLoadingState = isLoading || isFetching;
  const menuErrorMessage = isError
    ? error instanceof Error
      ? error.message
      : 'Unable to load menu.'
    : null;

  const sortedDays: MenuDay[] = menu?.days
    ? [...menu.days].sort((a, b) => a.day - b.day)
    : [];
  const hasMenuDays = sortedDays.length > 0;

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextValue: MenuViewMode | null,
  ) => {
    if (nextValue) {
      setViewMode(nextValue);
    }
  };

  const displayName = siteName || menu?.menuName || 'Community';

  return (
    <Box className="w-full">
      {pdfOpen && (
        <MenuPdfDialog
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          siteId={siteId}
          siteName={displayName}
          menuBugImageUrl={menuBugImageUrl}
        />
      )}
      <Paper className="rounded-none bg-[#0a0a0a]">
        <Box className="flex flex-col gap-4 px-4 pt-4 md:flex-row md:flex-wrap md:items-start md:justify-between">
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Property Menu
            </Typography>
          </Box>
          <Box>
            <Stack
              spacing={2}
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ alignItems: { sm: 'flex-end' } }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cycle Start Date
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {cycleStartDate ? formatDate(cycleStartDate) : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Property ID
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {propertyId || '—'}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => setPdfOpen(true)}
                size="large"
                sx={{ fontWeight: 700, px: 3 }}
              >
                Export Menu
              </Button>
            </Box>
          </Box>
        </Box>

        {syncError && (
          <Box className="px-4 pb-2">
            <Alert severity="error">{syncError}</Alert>
          </Box>
        )}
        {saveSuccess && !syncError && (
          <Box className="px-4 pb-2">
            <Alert severity="success">Menu settings saved.</Alert>
          </Box>
        )}
        {syncSuccess && !syncError && (
          <Box className="px-4 pb-2">
            <Alert severity="success">Menu sync requested successfully.</Alert>
          </Box>
        )}

        <Box className="px-4 pb-6">
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" fontSize={16} fontWeight={600}>
                Menu Details
              </Typography>
              {isLoadingState && <LinearProgress />}
              {menuErrorMessage && !isLoadingState && (
                <Alert severity="error">{menuErrorMessage}</Alert>
              )}
              {!isLoadingState && !menuErrorMessage && !menu && (
                <Typography color="text.secondary">
                  No menu data available for this site.
                </Typography>
              )}
              {!isLoadingState && menu && (
                <Stack spacing={1.5}>
                  <Typography variant="body2">
                    <strong>Property ID:</strong> {menu.propertyId ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cycle Start Date:</strong>{' '}
                    {formatDate(menu.cycleStartDate ?? '')}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Last Updated:</strong>{' '}
                    {menu.updatedOn
                      ? new Date(menu.updatedOn).toLocaleString()
                      : '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Menu Name:</strong> {menu.menuName ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Menu ID:</strong> {menu.diningRdMenuId ?? '—'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Days in Menu:</strong> {menu.daysInMenu ?? '—'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Box>

        {!isLoadingState && !menuErrorMessage && menu && (
          <Box className="px-4 pb-8">
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="h6" fontSize={16} fontWeight={600}>
                  Menu Overview
                </Typography>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChange}
                  size="small"
                >
                  <ToggleButton value="rows">
                    <ViewListIcon fontSize="small" sx={{ mr: 1 }} /> Rows
                  </ToggleButton>
                  <ToggleButton value="slides">
                    <SlideshowIcon fontSize="small" sx={{ mr: 1 }} /> Slides
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Divider sx={{ my: 3 }} />
              {hasMenuDays ? (
                viewMode === 'rows' ? (
                  <MenuRowsView
                    days={sortedDays}
                    cycleStartDate={menu.cycleStartDate}
                    daysInMenu={menu.daysInMenu}
                  />
                ) : (
                  <MenuSlidesView
                    days={sortedDays}
                    cycleStartDate={menu.cycleStartDate}
                    daysInMenu={menu.daysInMenu}
                  />
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No day-level menu data has been synced for this property yet.
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export { SiteMenusPage, type SiteMenusPageProps };
